// resources/js/services/errorLogger.js
import { router } from '@inertiajs/vue3'
import axios from 'axios'

class ErrorLogger {
  constructor() {
    this.isProduction = import.meta.env.MODE === 'production'
    this.enableAutoTickets = true // Auto-create tickets for critical errors
    this.errorQueue = []
    this.isProcessing = false
    this.maxRetries = 3
    this.recentErrors = new Map() // Track recent errors to prevent duplicates
    this.dedupeWindow = 5000 // 5 seconds deduplication window
  }

  /**
   * Log a JavaScript error
   */
  logJsError(error, info = {}) {
    const errorMessage = error.message || String(error)

    // Check if this error was already logged recently (deduplication)
    const errorKey = `${errorMessage}|${window.location.href}`
    const now = Date.now()

    if (this.recentErrors.has(errorKey)) {
      const lastReported = this.recentErrors.get(errorKey)
      if (now - lastReported < this.dedupeWindow) {
        console.debug('[ErrorLogger] Duplicate error suppressed:', errorMessage)
        return // Skip duplicate error
      }
    }

    // Mark this error as reported
    this.recentErrors.set(errorKey, now)

    // Clean up old entries (older than deduplication window)
    for (const [key, timestamp] of this.recentErrors.entries()) {
      if (now - timestamp > this.dedupeWindow) {
        this.recentErrors.delete(key)
      }
    }

    const currentUser = this.getCurrentUser()
    const errorData = {
      message: errorMessage,
      stack: error.stack || null,
      type: error.name || 'Error',
      url: window.location.href,
      route_name: this.getCurrentRouteName(),
      severity: this.determineSeverity(error),
      tenant_id: this.getTenantId(),
      tenant_user_id: currentUser?.id || null,
      reporter_name: currentUser?.name || null,
      reporter_email: currentUser?.email || null,
      project_name: this.getCurrentProjectName(),
      context: {
        ...info,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    }

    // Log to console in development
    if (!this.isProduction) {
      console.error('[ErrorLogger] JS Error:', error)
      console.error('[ErrorLogger] Context:', errorData.context)
    }

    // Send to server
    this.sendToServer('/admin/error-reports/js-error', errorData)
  }

  /**
   * Log an API error
   */
  logApiError(error, endpoint, method = 'GET') {
    const currentUser = this.getCurrentUser()
    const errorData = {
      endpoint: endpoint || error.config?.url || 'unknown',
      method: method || error.config?.method?.toUpperCase() || 'UNKNOWN',
      status_code: error.response?.status || 0,
      message: error.response?.data?.message || error.message || 'API request failed',
      response: JSON.stringify(error.response?.data || null),
      url: window.location.href,
      severity: error.response?.status >= 500 ? 'high' : 'medium',
      tenant_id: this.getTenantId(),
      tenant_user_id: currentUser?.id || null,
      reporter_name: currentUser?.name || null,
      reporter_email: currentUser?.email || null,
      project_name: this.getCurrentProjectName(),
      context: {
        request: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
        },
        response: error.response?.data,
        timestamp: new Date().toISOString(),
      },
    }

    // Log to console in development
    if (!this.isProduction) {
      console.error('[ErrorLogger] API Error:', errorData)
    }

    // Send to server
    this.sendToServer('/admin/error-reports/api-error', errorData)
  }

  /**
   * Report a bug manually
   */
  async reportBug(bugData) {
    const currentUser = this.getCurrentUser()
    const formData = new FormData()

    formData.append('title', bugData.title)
    formData.append('description', bugData.description)

    if (bugData.steps_to_reproduce) {
      formData.append('steps_to_reproduce', bugData.steps_to_reproduce)
    }

    if (bugData.expected_behavior) {
      formData.append('expected_behavior', bugData.expected_behavior)
    }

    if (bugData.actual_behavior) {
      formData.append('actual_behavior', bugData.actual_behavior)
    }

    formData.append('url', window.location.href)

    // Add tenant context
    const tenantId = this.getTenantId()
    if (tenantId) formData.append('tenant_id', tenantId)
    if (currentUser?.id) formData.append('tenant_user_id', currentUser.id)
    if (currentUser?.name) formData.append('reporter_name', currentUser.name)
    if (currentUser?.email) formData.append('reporter_email', currentUser.email)

    const projectName = this.getCurrentProjectName()
    if (projectName) formData.append('project_name', projectName)

    if (bugData.attachments && bugData.attachments.length > 0) {
      bugData.attachments.forEach((file) => {
        formData.append('attachments[]', file)
      })
    }

    // Send directly and return response (bypass queue for bug reports)
    try {
      const response = await axios.post('/admin/error-reports/bug', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response
    } catch (error) {
      throw error
    }
  }

  /**
   * Send error data to server
   */
  async sendToServer(endpoint, data, multipart = false) {
    // Add to queue
    this.errorQueue.push({ endpoint, data, retries: 0, multipart })

    // Process queue
    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  /**
   * Process error queue
   */
  async processQueue() {
    if (this.errorQueue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true
    const item = this.errorQueue[0]

    try {
      // Send directly to admin endpoint (no tenant prefix)
      const url = item.endpoint
      const headers = item.multipart
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' }

      const response = await axios.post(url, item.data, { headers })

      if (response.data.ticket_created) {
        console.info(`[ErrorLogger] Ticket created: ${response.data.ticket_number}`)
      }

      // Remove from queue on success
      this.errorQueue.shift()
    } catch (error) {
      console.error('[ErrorLogger] Failed to send error report:', error)

      // Retry logic
      item.retries++
      if (item.retries >= this.maxRetries) {
        console.error('[ErrorLogger] Max retries reached, discarding error report')
        this.errorQueue.shift()
      }
    }

    // Process next item
    this.processQueue()
  }

  /**
   * Determine error severity
   */
  determineSeverity(error) {
    const message = (error.message || String(error)).toLowerCase()

    if (
      message.includes('critical') ||
      message.includes('fatal') ||
      message.includes('cannot read') ||
      message.includes('undefined is not')
    ) {
      return 'critical'
    }

    if (
      message.includes('error') ||
      message.includes('failed') ||
      message.includes('exception')
    ) {
      return 'high'
    }

    if (message.includes('warning') || message.includes('deprecated')) {
      return 'medium'
    }

    return 'low'
  }

  /**
   * Get current route name
   */
  getCurrentRouteName() {
    try {
      // Use pathname instead of Ziggy route() to avoid errors
      if (router?.page?.props?.route) {
        return router.page.props.route
      }
      return window.location.pathname || null
    } catch (e) {
      return window.location.pathname || null
    }
  }

  /**
   * Get current user ID
   */
  getCurrentUserId() {
    try {
      if (router?.page?.props?.auth?.user) {
        return router.page.props?.auth?.user.id || null
      }
      return null
    } catch (e) {
      return null
    }
  }

  /**
   * Get current user object
   */
  getCurrentUser() {
    try {
      if (router?.page?.props?.auth) {
        return router.page.props?.auth?.user || null
      }
      return null
    } catch (e) {
      return null
    }
  }

  /**
   * Get tenant ID from current context
   */
  getTenantId() {
    try {
      // Source 1: Window global
      if (window.currentTenant?.id) {
        return window.currentTenant.id
      }

      // Source 2: Inertia page props (with extra safety checks)
      if (router?.page?.props?.tenant?.id) {
        return router.page.props?.tenant?.id
      }

      // Source 3: Extract from URL (tenant slug as fallback)
      const pathSegments = window.location.pathname.split('/').filter(Boolean)
      if (pathSegments.length > 0) {
        return pathSegments[0]
      }

      return null
    } catch (e) {
      // Silently fail - tenant context not available
      return null
    }
  }

  /**
   * Get tenant slug from current context
   */
  getTenantSlug() {
    try {
      // Source 1: Window global
      if (window.currentTenant?.slug) {
        return window.currentTenant.slug
      }

      // Source 2: Inertia page props (with extra safety checks)
      if (router?.page?.props?.tenant?.slug) {
        return router.page.props?.tenant?.slug
      }

      // Source 3: Extract from URL
      const pathSegments = window.location.pathname.split('/').filter(Boolean)
      if (pathSegments.length > 0) {
        return pathSegments[0]
      }

      return null
    } catch (e) {
      // Silently fail - tenant context not available
      return null
    }
  }

  /**
   * Get current project name (if available in context)
   */
  getCurrentProjectName() {
    try {
      // Try to get from current page props (with extra safety checks)
      if (router?.page?.props?.project) {
        return router.page.props.project.name || null
      }
      return null
    } catch (e) {
      return null
    }
  }
}

// Export singleton instance
export default new ErrorLogger()
